import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Copy, Users, Zap, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { CONTACT } from "@/data/content";

const copyEmail = async () => {
  try {
    await navigator.clipboard.writeText(CONTACT.email);
    toast.success("Adresse email copiée");
  } catch {
    toast.error("Copie impossible — sélectionnez l'adresse manuellement");
  }
};

const Contact = () => (
  <main data-testid="contact-page" className="pt-20">
    {/* ——— EN-TÊTE ——— */}
    <section className="relative overflow-hidden circuit-bg grain py-24 sm:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(15,52,140,0.08),transparent_55%)]" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.28em] text-brand-blue"
        >
          <span className="h-px w-10 bg-brand-blue/70" />
          Contact direct
        </motion.p>
        <h1 data-testid="contact-title" className="mt-8 font-display text-4xl sm:text-6xl lg:text-7xl font-black uppercase leading-[0.95] tracking-tight">
          <MaskedLines
            delay={0.3}
            lines={[
              <span key="a" className="text-ink">Pas de formulaire.</span>,
              <span key="b" className="text-brand-red glow-red">Des humains.</span>,
            ]}
          />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-700"
        >
          Chez EG 6.1, on préfère la conversation au formulaire. Appelez, écrivez :
          Eric ou Mickaël vous répondent en direct.
        </motion.p>
      </div>
    </section>

    {/* ——— COORDONNÉES ——— */}
    <section className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-6 lg:grid-cols-2">
        <Reveal>
          <a
            href={CONTACT.phoneHref}
            data-testid="contact-phone-card"
            className="group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-brand-blue/30 bg-brand-card p-10 transition-all duration-500 hover:shadow-[0_16px_60px_rgba(15,52,140,0.15)]"
          >
            <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-brand-blue/10 blur-3xl transition-all duration-500 group-hover:bg-brand-blue/20" aria-hidden="true" />
            <div className="flex items-center justify-between">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-blue shadow-[0_8px_24px_rgba(15,52,140,0.35)]">
                <Phone className="h-7 w-7 text-ink" />
              </span>
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-blue opacity-60" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-brand-blue" />
              </span>
            </div>
            <div className="mt-14">
              <p className="text-xs font-mono uppercase tracking-[0.28em] text-brand-blue">Téléphone — appel direct</p>
              <p className="mt-3 font-display text-3xl sm:text-5xl font-black tracking-tight text-ink transition-colors duration-300 group-hover:text-brand-blue">
                {CONTACT.phone}
              </p>
              <p className="mt-3 text-sm text-slate-600">Devis gratuit — dépannage & conseils sans engagement</p>
            </div>
          </a>
        </Reveal>

        <div className="grid gap-6">
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-slate-200 bg-brand-card p-8" data-testid="contact-email-card">
              <div className="flex items-center justify-between gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-ink border border-slate-200">
                  <Mail className="h-6 w-6 text-brand-blue" />
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={copyEmail}
                    data-testid="cta-email-copy"
                    className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 transition-colors hover:border-brand-blue/60 hover:text-brand-blue"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copier
                  </button>
                  <a
                    href={`mailto:${CONTACT.email}`}
                    data-testid="cta-email-send"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-red px-4 py-2 text-xs font-semibold text-ink transition-shadow hover:shadow-[0_0_24px_rgba(230,0,43,0.5)]"
                  >
                    Écrire
                  </a>
                </div>
              </div>
              <p className="mt-5 text-xs font-mono uppercase tracking-[0.28em] text-brand-blue">Email</p>
              <p className="mt-2 break-all font-display text-lg sm:text-2xl font-bold text-ink">{CONTACT.email}</p>
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-brand-card p-8" data-testid="contact-address-card">
                <MapPin className="h-6 w-6 text-brand-red" />
                <p className="mt-4 text-xs font-mono uppercase tracking-[0.28em] text-brand-blue">Atelier</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  {CONTACT.addressLine1}
                  <br />
                  {CONTACT.addressLine2}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-brand-card p-8" data-testid="contact-hours-card">
                <Clock className="h-6 w-6 text-brand-red" />
                <p className="mt-4 text-xs font-mono uppercase tracking-[0.28em] text-brand-blue">Horaires</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  Lun – Ven : 8h – 18h
                  <br />
                  Samedi : sur rendez-vous
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.24}>
            <div className="flex flex-wrap items-center gap-3" data-testid="contact-certifications">
              <span className="text-xs font-mono uppercase tracking-[0.25em] text-slate-600">Certifications</span>
              {CONTACT.certifications.map((c) => (
                <span
                  key={c}
                  data-testid={`contact-certification-${c.toLowerCase()}`}
                  className="inline-flex items-center gap-2 rounded-full border border-brand-blue/30 bg-brand-ink px-4 py-1.5 text-xs font-semibold text-brand-blue"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* ——— FONDATEURS + ZONE ——— */}
    <section className="border-t border-slate-200 bg-[#eaf1fa] py-20 sm:py-28 grain" data-testid="contact-zone">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-14 lg:grid-cols-2">
        <div>
          <Reveal>
            <p className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.28em] text-brand-blue">
              <Users className="h-4 w-4" /> Les fondateurs
            </p>
          </Reveal>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {CONTACT.founders.map((f, i) => (
              <Reveal key={f} delay={0.1 + i * 0.08}>
                <div
                  data-testid={`founder-card-${i}`}
                  className="rounded-2xl border border-slate-200 bg-brand-card p-7 transition-all duration-500 hover:-translate-y-1 hover:border-brand-blue/40"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-red/15 border border-brand-red/40 font-display text-lg font-black text-brand-red">
                    {f.split(" ").map((n) => n[0]).join("")}
                  </span>
                  <h3 className="mt-4 font-display text-xl font-bold text-ink">{f}</h3>
                  <p className="mt-1 text-sm text-slate-600">Co-fondateur — SARL EG 6.1</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div>
          <Reveal>
            <p className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.28em] text-brand-blue">
              <Zap className="h-4 w-4" /> Zone d'intervention
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="department-chips">
              {CONTACT.departments.map((d) => (
                <span
                  key={d}
                  className="rounded-full border border-brand-blue/40 bg-brand-ink px-5 py-2.5 text-sm font-semibold text-brand-blue"
                >
                  {d}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.14}>
            <div className="mt-5 flex flex-wrap gap-3" data-testid="zone-chips">
              {CONTACT.zone.map((z) => (
                <span
                  key={z}
                  className="rounded-full border border-slate-200 bg-brand-card px-5 py-2.5 text-sm text-slate-700 transition-colors duration-300 hover:border-brand-blue/50 hover:text-ink"
                >
                  {z}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-sm leading-relaxed text-slate-600">
              Basés à Urou et Crennes, nous intervenons dans l'Orne (61), le Calvados (14) et la Sarthe (72).
              Une urgence en dehors de la zone ? Appelez, on trouve une solution.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  </main>
);

export default Contact;
